import React, { useState, useEffect, useCallback } from 'react';
import { UI_COLORS } from '../utils/colors';
import PurchaseAdviceTable from '../components/purchase-advice/PurchaseAdviceTable';
import PurchaseAdviceActions from '../components/purchase-advice/PurchaseAdviceActions';
import PurchaseAdviceViewModal from '../components/purchase-advice/PurchaseAdviceViewModal';
import PurchaseAdviceEditModal from '../components/purchase-advice/PurchaseAdviceEditModal';

// Action Forms
import StartUnloadingForm from '../components/purchase-advice/StartUnloadingForm';
import CompleteUnloadingForm from '../components/purchase-advice/CompleteUnloadingForm';
import GenerateChallanForm from '../components/purchase-advice/GenerateChallanForm';
import AssignSupervisorModal from '../components/purchase-orders/AssignSupervisorModal';

// Common Components
import MessageAlert from '../components/common/MessageAlert';
import Pagination from '../components/common/Pagination';
import { useErrorDialog } from '../context/errorDialogContext';
import { useRole } from '../context/roleContext';

import usePurchaseAdvice from '../hooks/usePurchaseAdvice';
import useProductSizes from '../hooks/useProductSizes';
import { useTranslation } from 'react-i18next';

const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const DEFAULT_STATUS_FILTER = ['pending', 'ready_for_challan', 'supervisor_assigned', 'unloading', 'unloaded'];
const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'purchase_advice_page_size';

const PurchaseAdvicePage = () => {
    const { t } = useTranslation();

    const {
        purchaseAdvices,
        loading,
        error: hookError,
        fetchPurchaseAdvices,
        getPurchaseAdviceById,
        startUnloadingPurchaseAdvice,
        completeUnloadingPurchaseAdvice,
        generateChallanPurchaseAdvice,
        clearError,
        totalPages,
        totalCount,
    } = usePurchaseAdvice();

    const { productSizes, fetchProductSizes } = useProductSizes();
    const { showError } = useErrorDialog();
    const { userRole } = useRole();

    const [viewAdviceId, setViewAdviceId] = useState(null);
    const [viewAdviceData, setViewAdviceData] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);

    const [editAdviceId, setEditAdviceId] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    const todayDate = getTodayDateString();
    const [fromDateFilter, setFromDateFilter] = useState(todayDate);
    const [toDateFilter, setToDateFilter] = useState(todayDate);

    const [searchTerm, setSearchTerm] = useState('');
    const [committedSearch, setCommittedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('pending');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
    });

    // Modal States
    const [showAssignSupervisor, setShowAssignSupervisor] = useState(false);
    const [showStartUnloading, setShowStartUnloading] = useState(false);
    const [activeAdvice, setActiveAdvice] = useState(null);
    const [showCompleteUnloading, setShowCompleteUnloading] = useState(false);
    const [showEditUnloading, setShowEditUnloading] = useState(false);
    const [showGenerateChallan, setShowGenerateChallan] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);

    // Generic fetch logic
    const fetchData = useCallback((force = false) => {
        const skip = (currentPage - 1) * pageSize;
        fetchPurchaseAdvices({
            skip,
            limit: pageSize,
            ...(committedSearch && { search: committedSearch }),
            status: statusFilter ? [statusFilter] : DEFAULT_STATUS_FILTER,
            ...(fromDateFilter && { from_date: fromDateFilter }),
            ...(toDateFilter && { to_date: toDateFilter }),
        }, { force });
    }, [fetchPurchaseAdvices, currentPage, committedSearch, statusFilter, pageSize, fromDateFilter, toDateFilter]);

    useEffect(() => {
        fetchData();
        fetchProductSizes({ limit: 20 });
    }, [fetchData, fetchProductSizes]);

    const handleRefresh = useCallback(() => {
        fetchData(true);
    }, [fetchData]);

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
    };

    // Search debouncing
    useEffect(() => {
        const timer = setTimeout(() => {
            setCommittedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Status and date filter reset
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, fromDateFilter, toDateFilter]);

    // Handle generic errors from hook
    useEffect(() => {
        if (hookError) {
            setAlertMessage({ type: 'error', title: t('common.status.error'), message: hookError });
            clearError?.();
        }
    }, [hookError, clearError]);

    const handleViewAdvice = async (adviceOrId) => {
        const id = typeof adviceOrId === 'object' ? adviceOrId.id : adviceOrId;
        setViewAdviceId(id);
        setViewLoading(true);
        try {
            const result = await getPurchaseAdviceById(id);
            if (result.success) {
                setViewAdviceData(result.data);
            }
        } catch (err) {
            console.error('Error viewing advice:', err);
            showError(err?.message || t('common.alerts.fetchFailed'), t('common.alerts.errorTitle'));
        } finally {
            setViewLoading(false);
        }
    };

    const handleCloseViewModal = () => {
        setViewAdviceId(null);
        setViewAdviceData(null);
    };

    const handleEditAdvice = (adviceOrId) => {
        const id = typeof adviceOrId === 'object' ? adviceOrId.id : adviceOrId;
        setEditAdviceId(id);
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setEditAdviceId(null);
        setShowEditModal(false);
    };

    const handleUpdateSuccess = () => {
        handleRefresh();
    };

    // Action Handlers
    const handleAssignSupervisor = (advice) => {
        setActiveAdvice(advice);
        setShowAssignSupervisor(true);
    };

    const handleAssignSupervisorSubmit = (data) => {
        // Modal already handles the API call and closes itself
        // This is just called on success to refresh data and show message
        handleRefresh();
        setAlertMessage({ type: 'success', title: t('common.status.success'), message: 'Supervisor assigned successfully' });
    };

    const handleStartUnloading = (advice) => {
        setActiveAdvice(advice);
        setShowStartUnloading(true);
    };

    const handleCompleteUnloading = (advice) => {
        setActiveAdvice(advice);
        setShowCompleteUnloading(true);
    };

    const handleEditUnloading = (advice) => {
        setActiveAdvice(advice);
        setShowEditUnloading(true);
    };

    const handleStartUnloadingSubmit = async (payload) => {
        setActionLoading(true);
        try {
            const result = await startUnloadingPurchaseAdvice(activeAdvice.id, payload);
            if (result.success) {
                setShowStartUnloading(false);
                setActiveAdvice(null);
                handleRefresh();
                setAlertMessage({ type: 'success', title: t('common.status.success'), message: 'Unloading started successfully' });
            } else {
                console.error('Error starting unloading:', result.error);
                showError(result.error || 'Failed to start unloading', t('common.alerts.errorTitle'));
            }
        } catch (err) {
            console.error('Error starting unloading:', err);
            showError(err?.message || 'Failed to start unloading', t('common.alerts.errorTitle'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleCompleteUnloadingSubmit = async (payload) => {
        setActionLoading(true);
        try {
            const result = await completeUnloadingPurchaseAdvice(activeAdvice.id, payload);
            if (result.success) {
                setShowCompleteUnloading(false);
                setShowEditUnloading(false);
                setActiveAdvice(null);
                handleRefresh();
                setAlertMessage({ type: 'success', title: t('common.status.success'), message: 'Unloading completed successfully' });
            } else {
                showError(result.error || 'Failed to complete unloading', t('common.alerts.errorTitle'));
            }
        } catch (err) {
            console.error('Error completing unloading:', err);
            showError(err?.message || 'Failed to complete unloading', t('common.alerts.errorTitle'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleGenerateChallan = (advice) => {
        setActiveAdvice(advice);
        setShowGenerateChallan(true);
    };

    const handleGenerateChallanSubmit = async (payload) => {
        setActionLoading(true);
        try {
            const result = await generateChallanPurchaseAdvice(activeAdvice.id, payload);
            if (result.success) {
                setShowGenerateChallan(false);
                setActiveAdvice(null);
                handleRefresh();
                setAlertMessage({ type: 'success', title: t('common.status.success'), message: 'Challan generated and advice marked as complete' });
            } else {
                showError(result.error || 'Failed to generate challan', t('common.alerts.errorTitle'));
            }
        } catch (err) {
            console.error('Error generating challan:', err);
            showError(err?.message || 'Failed to generate challan', t('common.alerts.errorTitle'));
        } finally {
            setActionLoading(false);
        }
    };


    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {alertMessage && (
                <div className="fixed top-4 right-4 z-[9999]">
                    <MessageAlert
                        type={alertMessage.type}
                        title={alertMessage.title}
                        message={alertMessage.message}
                        onClose={() => setAlertMessage(null)}
                    />
                </div>
            )}

            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY || '#05445E' }}>
                        {t('navigation.pages.purchaseAdvice') || 'Purchase Advice'}
                    </h1>
                </div>
            </div>

            <div className="mb-6">
                <PurchaseAdviceActions
                    searchValue={searchTerm}
                    onSearchChange={setSearchTerm}
                    statusValue={statusFilter}
                    onStatusChange={setStatusFilter}
                    fromDate={fromDateFilter}
                    toDate={toDateFilter}
                    onFromDateChange={setFromDateFilter}
                    onToDateChange={setToDateFilter}
                    onRefresh={handleRefresh}
                />
            </div>

            <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6 overflow-hidden">
                <PurchaseAdviceTable
                    advices={purchaseAdvices}
                    loading={loading}
                    onViewAdvice={handleViewAdvice}
                    onEditAdvice={handleEditAdvice}
                    onAssignSupervisor={handleAssignSupervisor}
                    onStartUnloading={handleStartUnloading}
                    onCompleteUnloading={handleCompleteUnloading}
                    onEditUnloading={handleEditUnloading}
                    onGenerateChallan={handleGenerateChallan}
                    onRefresh={handleRefresh}
                    viewLoading={viewLoading}
                />
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={setCurrentPage}
                loading={loading}
                totalLabel="total advices"
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
            />

            {viewAdviceId && (
                <PurchaseAdviceViewModal
                    isOpen={!!viewAdviceId}
                    onClose={handleCloseViewModal}
                    data={viewAdviceData}
                    loading={viewLoading}
                />
            )}

            {showEditModal && (
                <PurchaseAdviceEditModal
                    isOpen={showEditModal}
                    onClose={handleCloseEditModal}
                    adviceId={editAdviceId}
                    onUpdateSuccess={handleUpdateSuccess}
                />
            )}

            {/* Status Action Modals */}
            {showAssignSupervisor && (
                <AssignSupervisorModal
                    isOpen={showAssignSupervisor}
                    onClose={() => { setShowAssignSupervisor(false); setActiveAdvice(null); }}
                    adviceId={activeAdvice?.id}
                    onAssigned={handleAssignSupervisorSubmit}
                />
            )}

            {showCompleteUnloading && (
                <CompleteUnloadingForm
                    isOpen={showCompleteUnloading}
                    onClose={() => { setShowCompleteUnloading(false); setActiveAdvice(null); }}
                    onSubmit={handleCompleteUnloadingSubmit}
                    adviceId={activeAdvice?.id}
                    productSizes={productSizes}
                />
            )}

            {showEditUnloading && (
                <CompleteUnloadingForm
                    isOpen={showEditUnloading}
                    onClose={() => { setShowEditUnloading(false); setActiveAdvice(null); }}
                    onSubmit={handleCompleteUnloadingSubmit}
                    adviceId={activeAdvice?.id}
                    productSizes={productSizes}
                    mode="edit"
                />
            )}

            {showStartUnloading && (
                <StartUnloadingForm
                    isOpen={showStartUnloading}
                    onClose={() => { setShowStartUnloading(false); setActiveAdvice(null); }}
                    onSubmit={handleStartUnloadingSubmit}
                    adviceId={activeAdvice?.id}
                    poNumber={activeAdvice?.po_number}
                    adviceNumber={activeAdvice?.advice_number}
                    remark={activeAdvice?.remark}
                    loading={actionLoading}
                />
            )}

            {showGenerateChallan && (
                <GenerateChallanForm
                    isOpen={showGenerateChallan}
                    onClose={() => { setShowGenerateChallan(false); setActiveAdvice(null); }}
                    onSubmit={handleGenerateChallanSubmit}
                    loading={actionLoading}
                    adviceId={activeAdvice?.id}
                    userRole={userRole}
                />
            )}
        </div>
    );
};

export default PurchaseAdvicePage;

